import axiosInstance from '@/api/axiosInstance'
import type { Group } from '@/features/rbac/types'

/**
 * Wire-level shape of GET /admin/me. Jackson serialises with snake_case so
 * the raw payload keys are snake_case; callers map to Redux's camelCase
 * user model at the view layer (useMe) — mirrors the rbacApi approach.
 */
export interface MeResponse {
  admin_id: number
  username: string | null
  phone_number: string | null
  email: string | null
  full_name: string | null
  status: string
  is_super_admin: boolean
  /** Single-entry `['*']` when is_super_admin. Otherwise an UPPER_SNAKE list. */
  permissions: string[]
  groups: Group[]
}

/**
 * Self-profile + effective permissions for the authenticated admin.
 *
 * Used by {@link useMe} to keep the Redux auth slice in sync with server-side
 * permission / status changes. The BE already Redis-caches the resolver, so
 * this stays cheap under a 60s polling interval. A 401 here means the session
 * is no longer usable (status flipped to INACTIVE/SUSPENDED, or account was
 * locked) — the axios interceptor will attempt a token refresh, and on refresh
 * failure it will purge state and bounce the user to /login.
 */
export const getMe = async (): Promise<MeResponse> => {
  const { data } = await axiosInstance.get<
    { data?: MeResponse } | MeResponse
  >('/admin/me')
  return unwrap<MeResponse>(data) as MeResponse
}

function unwrap<T>(raw: unknown): T | undefined {
  if (raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)) {
    const inner = (raw as { data?: T }).data
    if (inner !== undefined) return inner
  }
  return raw as T
}
